import React, { useEffect } from 'react';
import LogoMulticolor from '../../../../assets/images/logo/logo-multicolor.svg';
import { SidebarContainer, NavItem, LogoImage, SidebarHeader, Overlay, Category, LoginButtonWrapper } from './sidebar.styles';
import { useSwipeable } from 'react-swipeable';
import MaterialIcon from '../../../../components/material-icon';
import useCustomNavigate from '../../../../hooks/useCustomNavigate';
import { colorPalette } from '../../../../utils/theme';
import useInvestorDetailStore from '../../../../stores/investorDetailStore';
import CTAButton from '../../../../components/cta-button/cta-button';
import { usePWA } from '../../../../context/PWAContext';
import useIsMobile from '../../../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
    isSidebarVisible: boolean;
    toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isSidebarVisible, toggleSidebar }) => {
    const navigate = useCustomNavigate();
    const isMobile = useIsMobile();
    const isPWA = usePWA();
    const { t } = useTranslation();

    // Stores
    const { investor } = useInvestorDetailStore();

    // Prevent scrolling down
    useEffect(() => {
        if (isSidebarVisible) {
            document.body.style.overflow = 'hidden';

            const preventDefault = (e: TouchEvent) => {
                e.preventDefault();
            };

            document.addEventListener('touchmove', preventDefault, { passive: false });

            return () => {
                document.body.style.overflow = 'unset';
                document.removeEventListener('touchmove', preventDefault);
            };
        }
    }, [isSidebarVisible]);

    const handleItemClick = (url: string, reset: boolean) => {
        toggleSidebar();
        navigate(url, { replace: reset, clearHistory: reset });
    };

    // Swipeable configuration for mobile
    const handlers = useSwipeable({
        onSwipedLeft: () => toggleSidebar(),
        onSwipedRight: () => toggleSidebar(),
        delta: 30,
        trackMouse: true,
    });

    return (
        <>
            <SidebarContainer isVisible={isSidebarVisible} addBottomSpacing={isMobile && isPWA} {...handlers}>
                <SidebarHeader>
                    <LogoImage src={LogoMulticolor} alt="Logo" onClick={() => navigate("/", { replace: true, clearHistory: true })} />
                    <MaterialIcon
                        className="d-lg-none d-block"
                        icon="close"
                        size={24}
                        color={colorPalette.textMain}
                        onClick={toggleSidebar}
                    />
                </SidebarHeader>
                <ul className="nav flex-column">
                    <Category onClick={() => handleItemClick("/home", false)}>
                        {t('sidebar.categories.landlord')}
                    </Category>
                    <NavItem onClick={() => handleItemClick("/details#packages", false)}>
                        <button className="nav-link">{t('sidebar.landlord_nav.packages')}</button>
                    </NavItem>
                    <NavItem onClick={() => handleItemClick("/details#services", false)}>
                        <button className="nav-link">{t('sidebar.landlord_nav.services')}</button>
                    </NavItem>
                    <NavItem onClick={() => handleItemClick("/details#faq", false)}>
                        <button className="nav-link">{t('sidebar.landlord_nav.faq')}</button>
                    </NavItem>
                    <Category
                        onClick={() => handleItemClick("/tenant-home", false)}
                        topBorder
                    >
                        {t('sidebar.categories.tenant')}
                    </Category>
                    <NavItem onClick={() => handleItemClick("/tenant-home#available-units", false)}>
                        <button className="nav-link">{t('sidebar.tenant_nav.available_units')}</button>
                    </NavItem>
                    <NavItem onClick={() => handleItemClick("/tenant-home#how-it-works", false)}>
                        <button className="nav-link">{t('sidebar.tenant_nav.how_it_works')}</button>
                    </NavItem>
                    {/*<NavItem onClick={() => handleItemClick("/tenant-home#faq", false)}>
                        <button className="nav-link">FAQ</button>
                    </NavItem>*/}
                    {investor && (
                        <NavItem topBorder onClick={() => handleItemClick("/profile", true)}>
                            <button className="nav-link">
                                <MaterialIcon icon="account_circle" size={24} color={colorPalette.primaryDark} />
                                {t('sidebar.account')}
                            </button>
                        </NavItem>
                    )}
                </ul>
                {!investor && (
                    <LoginButtonWrapper>
                        <CTAButton
                            variant="primary"
                            size="L"
                            label={t('sidebar.login_button')}
                            onClick={() => navigate("/login")}
                            fullWidth
                        />
                    </LoginButtonWrapper>
                )}
            </SidebarContainer>
            {isSidebarVisible && <Overlay onClick={toggleSidebar} />}
        </>
    );
};

export default Sidebar;
